import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Layers, Sparkles, Coins, CalendarDays, Mail, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api, formatApiError } from "@/lib/api";
import { CATEGORIES } from "@/lib/constants";
import { useContent } from "@/lib/content";
import { ProductCard } from "@/components/shop/ProductCard";
import { CardIcon } from "@/components/shop/CardIcon";
import { EventRow, EventTabs } from "@/pages/EventsPage";

export const SectionHead = ({ eyebrow, title, color = "text-amber-600", children }) => (
  <div className="mb-5 flex flex-wrap items-end md:mb-8 justify-between gap-4">
    <div>
      <p className={`font-display text-sm uppercase tracking-[0.2em] ${color}`}>{eyebrow}</p>
      <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">{title}</h2>
    </div>
    {children}
  </div>
);

const Grid = ({ items, testid }) => (
  <div className="grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6 lg:grid-cols-4" data-testid={testid}>
    {items.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
  </div>
);

export const AnnouncementBar = () => {
  const [news, setNews] = useState([]);
  const cfg = useContent("/content/announcement", null);
  useEffect(() => {
    if (!cfg?.enabled || !cfg.show_news) return;
    Promise.all([api.get("/products", { params: { sort: "recenti", limit: 5, in_stock: true } }), api.get("/events", { params: { limit: 3 } })]).then(([p, e]) => {
      setNews([
        ...p.data.map((x) => ({ to: `/prodotto/${x.id}`, label: `Novità: ${x.name}` })),
        ...e.data.map((x) => ({ to: "/eventi", label: `Evento: ${x.title} – ${new Date(`${x.date}T00:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "short" })}` })),
      ]);
    }).catch(() => {});
  }, [cfg]);
  if (!cfg?.enabled) return null;
  const items = [...(cfg.text?.trim() ? [{ to: cfg.link || "", label: cfg.text, strong: true }] : []), ...(cfg.show_news ? news : [])];
  if (!items.length) return null;
  const Row = ({ hidden }) => (
    <div className="flex min-w-[100vw] shrink-0 items-center justify-around" aria-hidden={hidden}>
      {items.map((n, i) => {
        const cls = `flex items-center whitespace-nowrap px-6 ${n.to ? "hover:underline" : ""} ${n.strong ? "font-bold" : ""}`;
        const body = <><CardIcon className="mr-2 h-3.5 w-3 shrink-0" />{n.label}</>;
        if (!n.to) return <span key={i} className={cls}>{body}</span>;
        if (n.to.startsWith("https://")) return <a key={i} href={n.to} target="_blank" rel="noreferrer" tabIndex={hidden ? -1 : 0} className={cls}>{body}</a>;
        return <Link key={i} to={n.to} tabIndex={hidden ? -1 : 0} className={cls}>{body}</Link>;
      })}
    </div>
  );
  return (
    <div className="marquee overflow-hidden border-b-2 border-black bg-amber-500 py-2 font-display text-xs font-semibold text-black sm:text-sm" data-testid="announcement-bar">
      <div className="marquee-track flex w-max">
        <Row />
        <Row hidden />
      </div>
    </div>
  );
};

export const Trending = () => {
  const [cat, setCat] = useState("sigillati");
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/products", { params: { category: cat, limit: 8, sort: "valutazione", in_stock: true } }).then((r) => setItems(r.data)); }, [cat]);
  return (
    <section className="mx-auto mt-14 md:mt-24 max-w-[1440px] px-4 sm:px-6 lg:px-12">
      <SectionHead eyebrow="Di tendenza" title="I più cercati">
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button key={c.value} onClick={() => setCat(c.value)} data-testid={`trending-tab-${c.value}`}
              className={`rounded-full border-2 px-4 py-1.5 text-sm font-semibold ${cat === c.value ? "border-black bg-amber-500" : "border-black/10 bg-white text-stone-600"}`}>{c.label}</button>
          ))}
        </div>
      </SectionHead>
      <Grid items={items} testid="trending-grid" />
      <Link to={`/catalogo?category=${cat}`} className="mt-6 inline-block text-sm font-semibold text-amber-600 hover:underline" data-testid="trending-view-all">Vedi tutti →</Link>
    </section>
  );
};

const LINKS = [
  { to: "/deckbuilder", icon: Layers, title: "Costruisci un mazzo", text: "Incolla la tua lista", color: "bg-cyan-500", testid: "quick-deckbuilder" },
  { to: "/catalogo?sort=recenti", icon: Sparkles, title: "Nuove uscite", text: "Gli ultimi set disponibili", color: "bg-amber-500", testid: "quick-new" },
  { to: "/vendi", icon: Coins, title: "Vendi le tue carte", text: "Valutazione in negozio", color: "bg-violet-500", testid: "quick-sell" },
  { to: "/eventi", icon: CalendarDays, title: "Eventi e tornei", text: "Calendario settimanale", color: "bg-rose-500", testid: "quick-events" },
];

export const QuickLinks = () => (
  <section className="mx-auto mt-14 md:mt-24 grid max-w-[1440px] grid-cols-2 gap-4 px-4 sm:px-6 md:grid-cols-4 lg:px-12">
    {LINKS.map((l) => (
      <Link key={l.to} to={l.to} data-testid={l.testid} className="sticker-btn group rounded-2xl bg-white p-5">
        <span className={`flex h-11 w-11 items-center justify-center rounded-xl border-2 border-black ${l.color}`}><l.icon className="h-5 w-5 text-black" /></span>
        <p className="mt-4 font-display text-lg font-semibold leading-tight">{l.title}</p>
        <p className="text-sm text-stone-500">{l.text}</p>
        <ArrowRight className="mt-3 h-4 w-4 text-amber-600 transition-transform group-hover:translate-x-1" />
      </Link>
    ))}
  </section>
);

export const UpcomingEvents = () => {
  const [game, setGame] = useState("");
  const [events, setEvents] = useState([]);
  useEffect(() => { api.get("/events", { params: { game: game || undefined, limit: 5 } }).then((r) => setEvents(r.data)); }, [game]);
  return (
    <section className="mx-auto mt-14 md:mt-24 max-w-[1440px] px-4 sm:px-6 lg:px-12" data-testid="home-events">
      <SectionHead eyebrow="Unisciti a noi in negozio" title="Prossimi eventi" color="text-violet-500">
        <Link to="/eventi" className="text-sm font-semibold text-amber-600 hover:underline" data-testid="home-events-all">Calendario completo →</Link>
      </SectionHead>
      <EventTabs value={game} onChange={setGame} prefix="home-events" />
      <div className="mt-6 space-y-3">
        {events.length === 0 && <p className="text-stone-500">Nessun evento in programma.</p>}
        {events.map((e) => <EventRow key={e.id} e={e} />)}
      </div>
    </section>
  );
};

export const GameRow = ({ game, eyebrow }) => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/products", { params: { game, category: "singole", limit: 4, in_stock: true } }).then((r) => setItems(r.data)); }, [game]);
  if (!items.length) return null;
  return (
    <section className="mx-auto mt-14 md:mt-24 max-w-[1440px] px-4 sm:px-6 lg:px-12">
      <SectionHead eyebrow={eyebrow} title={`Carte singole ${game}`} color="text-cyan-600">
        <Link to={`/catalogo?game=${encodeURIComponent(game)}&category=singole`} className="text-sm font-semibold text-amber-600 hover:underline" data-testid={`game-row-all-${game}`}>Vedi tutte →</Link>
      </SectionHead>
      <Grid items={items} testid={`game-row-${game}`} />
    </section>
  );
};

export const FaqList = ({ prefix, triggerClass }) => {
  const faqs = useContent("/faqs", []) || [];
  return (
    <Accordion type="single" collapsible className="rounded-2xl border border-black/10 bg-white px-6" data-testid={`${prefix}-list`}>
      {faqs.map((f, i) => (
        <AccordionItem key={f.id} value={`f${i}`} className="border-black/10">
          <AccordionTrigger className={`text-left font-display text-base ${triggerClass}`} data-testid={`${prefix}-q-${i}`}>{f.q}</AccordionTrigger>
          <AccordionContent className="whitespace-pre-line text-stone-600">{f.a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};

export const FaqSection = () => (
  <section className="mx-auto mt-14 md:mt-24 max-w-[1000px] px-4 sm:px-6" data-testid="faq-section">
    <SectionHead eyebrow="Hai dubbi?" title="Domande frequenti" />
    <FaqList prefix="faq" triggerClass="font-semibold" />
  </section>
);

export const NewsletterSupport = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Iscrizione alla newsletter confermata!");
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="mx-auto mt-14 md:mt-24 grid max-w-[1440px] gap-6 px-4 sm:px-6 md:grid-cols-2 lg:px-12">
      <div className="rounded-3xl border-2 border-black bg-white p-6 shadow md:p-8-[6px_6px_0_#000]" data-testid="support-box">
        <LifeBuoy className="h-8 w-8 text-cyan-600" />
        <h3 className="mt-4 font-display text-2xl font-bold">Assistenza clienti</h3>
        <p className="mt-2 text-stone-600">Il nostro team è sempre pronto ad aiutarti con ordini, spedizioni e resi.</p>
        <a href="mailto:help.order@cardsource.eu?subject=Richiesta%20assistenza" data-testid="support-ticket-button" className="sticker-btn mt-6 inline-flex items-center gap-2 rounded-full bg-cyan-500 px-6 py-3 font-bold text-black"><Mail className="h-4 w-4" />Apri una richiesta</a>
      </div>
      <form onSubmit={submit} className="box-pattern rounded-3xl border-2 border-black p-6 md:p-8 shadow-[6px_6px_0_#000]" data-testid="newsletter-form">
        <h3 className="font-display text-2xl font-bold">Iscriviti alla newsletter</h3>
        <p className="mt-2 font-semibold text-black/70">Nuove uscite, promozioni ed esclusive: sii il primo a saperlo.</p>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="La tua email" data-testid="newsletter-email" className="h-12 flex-1 rounded-full border-2 border-black bg-white px-5 outline-none" />
          <button disabled={busy} data-testid="newsletter-submit" className="sticker-btn h-12 rounded-full bg-black px-6 font-bold text-white">Iscrivimi!</button>
        </div>
      </form>
    </section>
  );
};
